import { Getter } from '../Getter';
import { HttpError } from '../HttpError';
import { ApiBenchmarkReport, ApiBenchmark } from './Models/ApiBenchmarkReport';
import { BenchmarkReport, Benchmark } from './Models/BenchmarkReport';
import isValidUUID from 'uuid-validate';

type ApiResponse = ApiBenchmarkReport | null | undefined;

interface DataResponse {
  data?: BenchmarkReport;
  error?: never;
}

interface ErrorResponse {
  data?: never;
  error: HttpError;
}

export type { BenchmarkReport, Benchmark };
export type ClientOutput = DataResponse | ErrorResponse;

export class BenchmarkReportClient {
  private getter: Getter<ApiResponse | HttpError>;

  constructor(getter: Getter<ApiResponse | HttpError>) {
    this.getter = getter;
  }

  public async retrieveAsync(organizationUUID: string): Promise<ClientOutput> {
    if (!isValidUUID(organizationUUID)) {
      return {
        error: new HttpError(400),
      };
    }

    const response = await this.tryRetrieveAsync(organizationUUID);

    if (!response) {
      return {
        data: undefined,
        error: undefined,
      };
    }

    if (response instanceof HttpError) {
      return {
        error: response,
      };
    }

    return {
      data: this.toBenchmarkReport(response),
    };
  }

  private async tryRetrieveAsync(
    organizationUUID: string
  ): Promise<ApiResponse | HttpError> {
    const href = this.getHref(organizationUUID);

    return await this.getter.get(href);
  }

  private toBenchmarkReport(response: ApiBenchmarkReport): BenchmarkReport {
    const { period, benchmarks } = response;

    return {
      period: {
        type: period.type,
        from: new Date(period.from),
        value: period.value,
      },
      issueCatchRate: this.toBenchmark(
        benchmarks.find((benchmark) => benchmark.name === 'average_defects')
      ),
      codeReviewSize: this.toBenchmark(
        benchmarks.find(
          (benchmark) => benchmark.name === 'average_line_counts_per_review'
        )
      ),
      codeReviewLifecycleDuration: this.toBenchmark(
        benchmarks.find(
          (benchmark) => benchmark.name === 'average_review_lifecycle_hours'
        )
      ),
      reviewerResponseLatency: this.toBenchmark(
        benchmarks.find(
          (benchmark) => benchmark.name === 'average_reviewer_latency_hours'
        )
      ),
      authorTestsDistribution: this.toBenchmark(
        benchmarks.find(
          (benchmark) => benchmark.name === 'percent_test_lines_per_review'
        )
      ),
    };
  }

  private toBenchmark(benchmark?: ApiBenchmark): Benchmark | undefined {
    if (!benchmark) {
      return undefined;
    }

    return {
      dataType: benchmark.data_type,
      unit: benchmark.unit,
      positiveDirection: benchmark.positive_direction,
      name: benchmark.name,
      current: benchmark.current,
      previous: benchmark.previous,
      quartiles: benchmark.quartiles,
      cohortRank: benchmark.cohort_rank,
      improving: benchmark.improving,
    };
  }

  private getHref(organizationUUID: string): string {
    return `/api/v1/organizations/${organizationUUID}/benchmark-report`;
  }
}
